import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LocalizationDisplayData } from '.';
import {
  ControlIdentity,
  CplaceSingleLocalizedTextControlChange,
  CplaceSingleLocalizedTextControlParameters,
  Localization,
  LocalizedText,
} from '../../../api/model/models';
import { TranslationService } from '../../../i18n';
import { CplaceControlWithEditModeComponent, CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR } from '../shared';
import { CplaceControlService } from '../shared/cplace-control.service';

@Component({
  selector: 'cf-frontend-sdk-cplace-single-localized-text-control',
  templateUrl: './cplace-single-localized-text-control.component.html',
  styleUrls: ['./styles/cplace-single-localized-text-control.component.scss'],
  providers: [CPLACE_CONTROL_COMPONENT_VALUE_ACCESSOR(CplaceSingleLocalizedTextControlComponent)],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class CplaceSingleLocalizedTextControlComponent extends CplaceControlWithEditModeComponent<LocalizedText> {
  public static readonly componentName = 'CplaceSingleLocalizedTextControlComponent';

  /**
   * Whether the control should use a text area.
   */
  @Input() public isTextArea = false;

  @ViewChildren('cplaceSingleLocalizedTextTextElement', { read: ElementRef })
  private textElements!: QueryList<ElementRef>;

  @HostBinding('class.cf-frontend-sdk-cplace-single-localized-text-control')
  public cplaceSingleLocalizedTextControl = true;

  /**
   * Mirror of localizations in value. This is meant for display purposes only and should not be touched.
   * Please use {@code value} instead.
   */
  public localizedText: LocalizedText = this.emptyLocalizedText();

  public $localizationsDisplayData: Observable<LocalizationDisplayData[]> = combineLatest([this.$value, this.$label, this.$editMode]).pipe(
    map(([latestValue, latestLabel, latestEditMode]) => {
      return this.updateLocalizationDisplayData(latestValue?.supportedLocalizations, latestLabel, latestEditMode);
    })
  );

  constructor(
    private _translationService: TranslationService,
    public elementRef: ElementRef<HTMLElement>,
    public changeDetectorRef: ChangeDetectorRef,
    public cplaceControlService: CplaceControlService
  ) {
    super(elementRef, changeDetectorRef, cplaceControlService);
    this.$value.pipe(untilDestroyed(this)).subscribe((value) => {
      if (!value) {
        this.localizedText = this.emptyLocalizedText();
        return;
      }

      if (this.cplaceControlService.deepEqual(value, this.localizedText)) {
        return;
      }

      const cloned = this.cplaceControlService.deepClone(value);

      if (cloned) {
        this.localizedText = cloned;
      } else {
        this.localizedText = this.emptyLocalizedText();
      }
    });
  }

  private static clearFocusedTextElement(textControl: ElementRef) {
    if (textControl.nativeElement === document.activeElement) {
      textControl.nativeElement.value = '';
    }
  }

  /**
   * ngFor trackBy method
   * @param index Localization display data index
   * @param localizationDisplayData Localization display data
   */
  public localizationsDisplayDataTrackBy(index: number, localizationDisplayData: LocalizationDisplayData): string {
    return localizationDisplayData.languageId;
  }

  public onNgModelChange() {
    this.value = this.cplaceControlService.deepClone(this.localizedText);
  }

  protected initializeControlParameters(controlParameters: CplaceSingleLocalizedTextControlParameters): void {
    this.value = controlParameters.value;
    this.isTextArea = controlParameters.isTextArea;
  }

  protected beforeSubmitHook(): void {
    this.textElements.forEach((textInput) => textInput.nativeElement.blur());
  }

  protected onCancelHook(): void {
    this.textElements.forEach((textInput) => textInput.nativeElement.blur());
  }

  protected getControlChange(identity: ControlIdentity, value: LocalizedText): CplaceSingleLocalizedTextControlChange {
    return { featureName: identity.featureName, componentName: CplaceSingleLocalizedTextControlComponent.componentName, value: value };
  }

  protected getComponentClassName(): string {
    return CplaceSingleLocalizedTextControlComponent.componentName;
  }

  protected onClear(): void {
    this.textElements.forEach((textInput) => CplaceSingleLocalizedTextControlComponent.clearFocusedTextElement(textInput));
  }

  /**
   * Updates the localization display data
   * @param latestValue Localization value
   * @param latestLabel Localization label
   * @param latestEditMode Edit mode
   */
  private updateLocalizationDisplayData(
    latestValue: Localization[] | undefined,
    latestLabel: string,
    latestEditMode: boolean
  ): LocalizationDisplayData[] {
    const data: LocalizationDisplayData[] = [];
    latestValue?.forEach((localization) => {
      const language = this._translationService.getInstantTranslation(
        `cf-frontend-sdk.shared.languages.${localization.languageId.toLowerCase()}`
      );
      data.push({
        languageId: localization.languageId,
        showEmptyValue: !localization.value && !latestEditMode,
        placeholder: this._translationService.getInstantTranslation('cf-frontend-sdk.cplace-single-localized-text-control.placeholder', {
          language: language,
        }),
        label: `${latestLabel} (${language})`,
      });
    });
    return data;
  }

  /**
   * Creates an empty {@link LocalizedText} object.
   * @private
   */
  private emptyLocalizedText(): LocalizedText {
    return {
      supportedLocalizations: [],
      unsupportedLocalizations: [],
    };
  }
}
